import React from 'react';
import moment, { isMoment, Moment } from 'moment';
import { useController, useFormContext } from 'react-hook-form';
import DatePickerReact, { registerLocale } from 'react-datepicker';
import { cs, sk, es, enGB, hu, uk, ru, de, fr } from 'date-fns/locale';
import { useAppContext } from '../../../../Services/AppContext';
import { joinClasses } from '../../../../Utils/className.util';
import TopFieldContent from '../TopFieldContent';
import ErrorMessage from '../ErrorMessage';
import useTranslation from '../../../../Hooks/useTranslation';
import useFormName from '../../../../Hooks/useFormName';
// @ts-ignore
import styles from './DateField.module.scss';
import 'react-datepicker/dist/react-datepicker.css';
import InputWrapper from '../InputWrapper/InputWrapper';

const languages = {
	cs: cs,
	sk: sk,
	hu: hu,
	en: enGB,
	ru: ru,
	uk: uk,
	es: es,
	de: de,
	fr: fr,
};
for (const language in languages) {
	registerLocale(language, languages[language]);
}

interface IDatePicker {
	onChange?: any;
	value?: Moment;
	placeholder?: string;
	required?: boolean;
	usePortal?: boolean;
	minDate?: Moment;
	maxDate?: Moment;
	name: string;
	label?: string;
	className?: string;
	isRequired?: boolean;
	isWrapped?: boolean;
	isDisabled?: boolean;
}

const DateField = ({
	name,
	label,
	usePortal,
	placeholder,
	minDate,
	maxDate,
	className,
	isRequired,
	isWrapped,
	isDisabled,
	value,
}: IDatePicker) => {
	const { language } = useAppContext();
	const { t } = useTranslation();

	const { fieldName } = useFormName({ name });
	const { control } = useFormContext();

	const {
		field,
		fieldState: { error },
	} = useController({
		control,
		name: fieldName,
		defaultValue: value || '',
	});

	const handleOnChange = (e: any) => {
		field.onChange(moment(e).toISOString());
	};

	const selectedDate =
		field.value && moment(field.value).isValid()
			? moment(field.value).toDate()
			: null;

	const component = (
		<div className={className}>
			{label && <TopFieldContent label={label} isRequired={isRequired} />}
			<DatePickerReact
				minDate={minDate?.toDate()}
				maxDate={maxDate?.toDate()}
				placeholderText={placeholder}
				portalId={usePortal ? 'datepicker-portal' : undefined}
				selected={selectedDate}
				dateFormat={t('base.dateFormatJsReact')}
				locale={language}
				onChange={handleOnChange}
				className={joinClasses([
					'form-control',
					styles.input,
					error?.message ? styles.error : undefined,
				])}
				disabled={isDisabled}
			/>
			{error?.message && <ErrorMessage tx={error.message} />}
		</div>
	);

	if (isWrapped) {
		return <InputWrapper>{component}</InputWrapper>;
	}

	return component;
};

export default DateField;
